import type {
  MockCache,
  OpEntityTable,
  TaskId,
  ChecklistItemId,
  CommentId,
  TaskLinkId,
  TimeEntryId,
  ChannelId,
  MessageId,
  ReactionId,
  InboxItemId,
  Task,
  ChecklistItem,
  Comment,
  TaskLink,
  TimeEntry,
  Subscription,
  Channel,
  ChannelMember,
  Message,
  Reaction,
  InboxItem,
  UserId,
  ActivityEvent,
} from '../types';

import { activityTaskIds, addToIndex, idxPull, removeFromIndex } from './indexes';
import { taskKeys } from './internal';
import { bump, requireCache } from './runtime';

function tableDelete(cache: MockCache, table: OpEntityTable, entityId: string): void {
  switch (table) {
    case 'task':
      cache.tasks.delete(entityId as TaskId);
      return;
    case 'checklistItem':
      cache.checklist.delete(entityId as ChecklistItemId);
      return;
    case 'comment':
      cache.comments.delete(entityId as CommentId);
      return;
    case 'taskLink':
      cache.links.delete(entityId as TaskLinkId);
      return;
    case 'timeEntry':
      cache.timeEntries.delete(entityId as TimeEntryId);
      return;
    case 'subscription':
      cache.subscriptions.delete(entityId);
      return;
    case 'channel':
      cache.channels.delete(entityId as ChannelId);
      return;
    case 'channelMember':
      cache.members.delete(entityId);
      return;
    case 'message':
      cache.messages.delete(entityId as MessageId);
      return;
    case 'reaction':
      cache.reactions.delete(entityId as ReactionId);
      return;
    case 'inboxItem':
      cache.inbox.delete(entityId as InboxItemId);
      return;
    default:
      return;
  }
}

function tableRestore(cache: MockCache, table: OpEntityTable, entityId: string, row: Record<string, unknown>): void {
  const record = row as unknown as object;
  switch (table) {
    case 'task':
      cache.tasks.set(entityId as TaskId, record as Task);
      return;
    case 'checklistItem':
      cache.checklist.set(entityId as ChecklistItemId, record as ChecklistItem);
      return;
    case 'comment':
      cache.comments.set(entityId as CommentId, record as Comment);
      return;
    case 'taskLink':
      cache.links.set(entityId as TaskLinkId, record as TaskLink);
      return;
    case 'timeEntry':
      cache.timeEntries.set(entityId as TimeEntryId, record as TimeEntry);
      return;
    case 'subscription':
      cache.subscriptions.set(entityId, record as Subscription);
      return;
    case 'channel':
      cache.channels.set(entityId as ChannelId, record as Channel);
      return;
    case 'channelMember':
      cache.members.set(entityId, record as ChannelMember);
      return;
    case 'message':
      cache.messages.set(entityId as MessageId, record as Message);
      return;
    case 'reaction':
      cache.reactions.set(entityId as ReactionId, record as Reaction);
      return;
    case 'inboxItem':
      cache.inbox.set(entityId as InboxItemId, record as InboxItem);
      return;
    default:
      break;
  }
  if (table === 'user') {
    const user = cache.users.get(entityId as UserId);
    if (user !== undefined) {
      Object.assign(user, record);
    }
  }
}

function tableRow(cache: MockCache, table: OpEntityTable, entityId: string): Record<string, unknown> | undefined {
  switch (table) {
    case 'task':
      return cache.tasks.get(entityId as TaskId) as unknown as Record<string, unknown> | undefined;
    case 'checklistItem':
      return cache.checklist.get(entityId as ChecklistItemId) as unknown as Record<string, unknown> | undefined;
    case 'comment':
      return cache.comments.get(entityId as CommentId) as unknown as Record<string, unknown> | undefined;
    case 'taskLink':
      return cache.links.get(entityId as TaskLinkId) as unknown as Record<string, unknown> | undefined;
    case 'timeEntry':
      return cache.timeEntries.get(entityId as TimeEntryId) as unknown as Record<string, unknown> | undefined;
    case 'subscription':
      return cache.subscriptions.get(entityId) as unknown as Record<string, unknown> | undefined;
    case 'channel':
      return cache.channels.get(entityId as ChannelId) as unknown as Record<string, unknown> | undefined;
    case 'channelMember':
      return cache.members.get(entityId) as unknown as Record<string, unknown> | undefined;
    case 'message':
      return cache.messages.get(entityId as MessageId) as unknown as Record<string, unknown> | undefined;
    case 'reaction':
      return cache.reactions.get(entityId as ReactionId) as unknown as Record<string, unknown> | undefined;
    case 'inboxItem':
      return cache.inbox.get(entityId as InboxItemId) as unknown as Record<string, unknown> | undefined;
    case 'user':
      return cache.users.get(entityId as UserId) as unknown as Record<string, unknown> | undefined;
    default:
      return undefined;
  }
}

function undoKeys(cache: MockCache, event: ActivityEvent): string[] {
  const keys: string[] = [];
  const entityId = event.entityId;
  switch (event.entityTable) {
    case 'task':
      keys.push(...taskKeys(cache, entityId as TaskId));
      break;
    case 'checklistItem': {
      const row = cache.checklist.get(entityId as ChecklistItemId);
      if (row !== undefined) {
        keys.push(...taskKeys(cache, row.taskId));
      }
      break;
    }
    case 'comment': {
      const row = cache.comments.get(entityId as CommentId);
      if (row !== undefined) {
        keys.push(...taskKeys(cache, row.taskId));
      }
      break;
    }
    case 'taskLink': {
      const before = (event.before ?? {}) as Record<string, unknown>;
      const from = before['fromTaskId'];
      const to = before['toTaskId'];
      if (typeof from === 'string') {
        keys.push(...taskKeys(cache, from as TaskId));
      }
      if (typeof to === 'string' && to !== from) {
        keys.push(...taskKeys(cache, to as TaskId));
      }
      break;
    }
    case 'timeEntry': {
      const row = cache.timeEntries.get(entityId as TimeEntryId);
      if (row !== undefined) {
        keys.push(...taskKeys(cache, row.taskId));
      } else {
        const before = (event.before ?? {}) as Record<string, unknown>;
        const taskId = before['taskId'];
        if (typeof taskId === 'string') {
          keys.push(...taskKeys(cache, taskId as TaskId));
        }
      }
      break;
    }
    case 'message': {
      const row = cache.messages.get(entityId as MessageId);
      if (row !== undefined) {
        keys.push(`channel:${String(row.channelId)}`);
        if (row.threadRootId !== null) {
          keys.push(`thread:${String(row.threadRootId)}`);
        }
      }
      break;
    }
    case 'reaction': {
      keys.push('inbox');
      break;
    }
    case 'channel':
    case 'channelMember':
      keys.push(`channel:${String(entityId).split(':')[0]}`);
      break;
    case 'inboxItem':
      keys.push('inbox');
      break;
    case 'user':
      keys.push('presence');
      break;
    default:
      break;
  }
  return keys;
}

function takeLastOwnEvent(cache: MockCache): ActivityEvent | undefined {
  for (let index = cache.activity.length - 1; index >= 0; index -= 1) {
    const event = cache.activity[index];
    if (event !== undefined && event.actorId === cache.me) {
      cache.activity.splice(index, 1);
      return event;
    }
  }
  return undefined;
}

export function undoLast(): void {
  const cache = requireCache();
  const event = takeLastOwnEvent(cache);
  if (event === undefined) {
    return;
  }
  if (event.mutator === 'presence.set' || event.mutator === 'presence.typing') {
    const presence = cache.presence.get(event.actorId);
    if (event.op === 'insert') {
      cache.presence.delete(event.actorId);
    } else if (presence !== undefined && event.before !== null) {
      Object.assign(presence, event.before);
    }
    for (const taskId of activityTaskIds(event)) {
      idxPull(cache.index.activityByTask, taskId, event.id);
    }
    bump('presence');
    return;
  }
  for (const taskId of activityTaskIds(event)) {
    idxPull(cache.index.activityByTask, taskId, event.id);
  }
  if (event.op === 'insert') {
    removeFromIndex(cache, event.entityTable, event.entityId);
    tableDelete(cache, event.entityTable, event.entityId);
  } else if (event.op === 'delete') {
    if (event.before !== null) {
      tableRestore(cache, event.entityTable, event.entityId, event.before);
      addToIndex(cache, event.entityTable, event.entityId);
    }
  } else {
    const row = tableRow(cache, event.entityTable, event.entityId);
    if (row !== undefined && event.before !== null) {
      removeFromIndex(cache, event.entityTable, event.entityId);
      Object.assign(row, event.before);
      addToIndex(cache, event.entityTable, event.entityId);
    }
  }
  const keys = undoKeys(cache, event);
  if (keys.length > 0) {
    bump(...keys);
  }
}
